import LocalOfferOutlined from '@mui/icons-material/LocalOfferOutlined';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { ChartsTooltipContainer, useItemTooltip } from '@mui/x-charts-pro';
import { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { RetentionVisitViewRow } from './retention.utils';

/**
 * Rows backing the conversion chart, keyed by data index. The tooltip is
 * mounted by the chart via the `slots.tooltip` API, so it can't receive the
 * rows as props — the chart provides them through this context instead.
 */
export const ConversionTooltipRowsContext = createContext<
  RetentionVisitViewRow[]
>([]);

/**
 * True when the bars are clickable (a bar action is wired) — drives the
 * "click to draft an offer" affordance in the hover tooltip. Provided by the
 * chart the same way as the rows, since the tooltip is slotted in.
 */
export const ConversionTooltipClickableContext = createContext(false);

const ConversionTooltipContent = () => {
  const { t } = useTranslation();
  const rows = useContext(ConversionTooltipRowsContext);
  const clickable = useContext(ConversionTooltipClickableContext);
  const tooltip = useItemTooltip<'bar'>();

  const row = tooltip ? rows[tooltip.identifier.dataIndex] : undefined;
  if (!row) return null;

  const lines: [string, string][] = [
    [
      t('page.guestJourney.retention.conversionChart.tooltip.customers'),
      row.customerCount == null ? '—' : row.customerCount.toLocaleString(),
    ],
    [
      t('page.guestJourney.retention.conversionChart.tooltip.conversion'),
      row.conversionFromLastVisitPct == null
        ? '—'
        : `${Math.round(row.conversionFromLastVisitPct * 100)}%`,
    ],
    [
      t('page.guestJourney.retention.conversionChart.tooltip.medianDays'),
      row.medianDaysFromLastVisit == null
        ? '—'
        : `${row.medianDaysFromLastVisit}d`,
    ],
    [
      t('page.guestJourney.retention.conversionChart.tooltip.meanDays'),
      row.meanDaysFromLastVisit == null ? '—' : `${row.meanDaysFromLastVisit}d`,
    ],
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        m: 1,
        border: 'solid',
        borderWidth: 2,
        borderColor: 'divider',
        overflow: 'hidden',
      }}
      data-testid="Retention.ConversionChart.Tooltip"
    >
      <Box
        sx={{
          borderBottom: 'solid',
          borderWidth: 2,
          borderColor: 'divider',
          px: 1.5,
          py: 0.75,
        }}
      >
        <Typography variant="body2" fontWeight={700}>
          {t('page.guestJourney.retention.conversionChart.tooltip.visit', {
            visit: row.visitNumber,
          })}
        </Typography>
      </Box>
      <Box display="grid" rowGap={0.5} px={1.5} py={0.75}>
        {lines.map(([label, value]) => (
          <Box
            key={label}
            display="flex"
            justifyContent="space-between"
            columnGap={2}
          >
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="caption" fontWeight={700}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
      {clickable && (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{
            borderTop: 'solid',
            borderWidth: 2,
            borderColor: 'divider',
            px: 1.5,
            py: 0.75,
          }}
          data-testid="Retention.ConversionChart.Tooltip.ClickHint"
        >
          <LocalOfferOutlined fontSize="small" color="primary" />
          <Typography variant="caption" color="primary" fontWeight={700}>
            {t(
              'page.guestJourney.retention.conversionChart.tooltip.clickToDraft',
            )}
          </Typography>
        </Stack>
      )}
    </Paper>
  );
};

/** Item-triggered tooltip slotted into the conversion BarChart. */
const ConversionByVisitTooltip = () => (
  <ChartsTooltipContainer trigger="item">
    <ConversionTooltipContent />
  </ChartsTooltipContainer>
);

export default ConversionByVisitTooltip;
